import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { App, Avatar, Button, Card, Col, Empty, List, Popconfirm, Row, Segmented, Select, Skeleton, Space, Tooltip, Typography } from 'antd';
import { LoadError, isNotFound, tableLoading } from '../../components/states';
import { DeleteOutlined, InboxOutlined, MailOutlined } from '@ant-design/icons';
import { TableSearch } from '../../components/NameCell';
import { palette } from '../../app/theme';

const initials = (s: string) => s.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';
import dayjs from 'dayjs';
import type { ThreadItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { ThreadView } from '../../components/ThreadView';
import { useDeleteThread, useInboxSummary, useThread, useThreads, useUpdateThread } from '../../features/email/api';
import { errorMessage } from '../../api/client';

export function InboxPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const summary = useInboxSummary();
  const domainId = sp.get('domain') ?? undefined;
  const status = (sp.get('status') as 'inbox' | 'unread' | 'archived' | 'all' | null) ?? 'inbox';
  const q = sp.get('q') ?? undefined;
  const selected = sp.get('thread') ?? undefined;
  const [page, setPage] = useState(1);
  const threads = useThreads({ domainId, status, q, page, pageSize: 30 });
  const thread = useThread(selected);
  const update = useUpdateThread();
  const remove = useDeleteThread();

  const set = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    setSp(next, { replace: true });
  };
  useEffect(() => setPage(1), [domainId, status, q]);
  // Opening a thread marks it read on the server; refresh the list and the unread badges.
  const openedId = thread.data?.id;
  useEffect(() => {
    if (!openedId) return;
    void qc.invalidateQueries({ queryKey: ['threads'] });
    void qc.invalidateQueries({ queryKey: ['inbox-summary'] });
  }, [openedId, qc]);

  const domains = summary.data?.domains ?? [];

  return (
    <div className="page page-wide">
      <PageHeader
        title="Inbox"
        subtitle="Replies and new mail received on your domains. Any admin can answer as any agent."
        extra={
          <Select
            value={domainId ?? 'all'}
            style={{ minWidth: 220 }}
            onChange={(v) => set({ domain: v === 'all' ? undefined : v, thread: undefined })}
            options={[
              { value: 'all', label: `All domains${summary.data ? ` (${summary.data.totalUnread} unread)` : ''}` },
              ...domains.map((d) => ({ value: d.id, label: `${d.domain}${d.unread ? ` (${d.unread} unread)` : ''}` })),
            ]}
          />
        }
      />
      <Row gutter={24}>
        <Col xs={24} lg={9} xl={8}>
          <Card styles={{ body: { padding: 0 } }}>
            <div style={{ padding: 16, borderBottom: `1px solid ${palette.border}` }}>
              <Space orientation="vertical" style={{ width: '100%' }} size={12}>
                <TableSearch value={q} onSearch={(v) => set({ q: v || undefined })} placeholder="Search subject, sender, contact" width={9999} />
                <Segmented block value={status} onChange={(v) => set({ status: String(v) })} options={[{ value: 'inbox', label: 'Inbox' }, { value: 'unread', label: 'Unread' }, { value: 'archived', label: 'Archived' }, { value: 'all', label: 'All' }]} />
              </Space>
            </div>
            <List<ThreadItem>
              split={false}
              style={{ padding: 8 }}
              loading={tableLoading(threads)}
              dataSource={threads.data?.items ?? []}
              locale={{
                emptyText: threads.isError ? (
                  <div style={{ padding: 12 }}><LoadError error={threads.error} onRetry={threads.refetch} /></div>
                ) : (
                  <Empty
                    image={<InboxOutlined style={{ fontSize: 32, color: palette.textTertiary }} />}
                    description={q ? `No threads match “${q}”` : status === 'unread' ? 'No unread threads' : status === 'archived' ? 'No archived threads' : 'No threads yet. Replies to your agents land here.'}
                  />
                ),
              }}
              pagination={threads.data && threads.data.total > 30 ? { current: page, pageSize: 30, total: threads.data.total, onChange: setPage, size: 'small', style: { padding: 8 } } : false}
              renderItem={(t) => {
                const name = t.contact ? [t.contact.firstName, t.contact.lastName].filter(Boolean).join(' ') || t.contact.email : t.lastMessage?.fromAddress ?? '—';
                const active = t.id === selected;
                return (
                  <List.Item
                    onClick={() => set({ thread: t.id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        set({ thread: t.id });
                      }
                    }}
                    tabIndex={0}
                    aria-current={active ? 'true' : undefined}
                    style={{ cursor: 'pointer', padding: '12px', borderRadius: 12, marginBottom: 2, background: active ? palette.primarySoft : undefined, alignItems: 'flex-start', gap: 12, display: 'flex' }}
                  >
                    <Avatar size={40} style={{ background: active ? palette.surface : palette.primarySoft, color: palette.primary, fontWeight: 600, flex: 'none' }}>{initials(name ?? "?")}</Avatar>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <Typography.Text strong={t.unread} ellipsis style={{ flex: 1 }}>
                          {name}
                        </Typography.Text>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
                          <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{dayjs(t.lastMessageAt).format('MMM D')}</Typography.Text>
                          {t.unread && <span aria-label="Unread" style={{ width: 8, height: 8, borderRadius: 4, background: palette.primary, display: 'inline-block' }} />}
                        </span>
                      </div>
                      <Typography.Text ellipsis style={{ display: 'block', fontWeight: t.unread ? 600 : 400 }}>{t.subject}</Typography.Text>
                      <Typography.Text type="secondary" ellipsis style={{ display: 'block', fontSize: 12 }}>
                        {t.lastMessage?.direction === 'OUT' ? '↗ ' : ''}{t.lastMessage?.snippet ?? ''}
                      </Typography.Text>
                      {!domainId && <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t.domain.domain}{t.messageCount > 1 ? ` · ${t.messageCount} messages` : ''}</Typography.Text>}
                    </div>
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={15} xl={16}>
          {!selected ? (
            <Card style={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Empty image={<MailOutlined style={{ fontSize: 40, color: palette.textTertiary }} />} description="Select a thread to read it" />
            </Card>
          ) : thread.isError ? (
            <Card>
              {isNotFound(thread.error) ? (
                <Empty description="This thread no longer exists. It may have been deleted." />
              ) : (
                <LoadError error={thread.error} onRetry={thread.refetch} title="Could not load this thread" />
              )}
            </Card>
          ) : thread.isLoading || !thread.data ? (
            <Card><Skeleton active /></Card>
          ) : (
            <Card
              extra={
                <Space>
                  <Tooltip title={thread.data.unread ? 'Mark as read' : 'Mark as unread'}>
                    <Button size="small" loading={update.isPending && update.variables?.unread !== undefined} onClick={() => update.mutateAsync({ id: thread.data!.id, unread: !thread.data!.unread }).catch((e) => message.error(errorMessage(e)))}>
                      {thread.data.unread ? 'Mark read' : 'Mark unread'}
                    </Button>
                  </Tooltip>
                  <Button size="small" loading={update.isPending && update.variables?.archived !== undefined} onClick={() => update.mutateAsync({ id: thread.data!.id, archived: !thread.data!.archived }).catch((e) => message.error(errorMessage(e)))}>
                    {thread.data.archived ? 'Unarchive' : 'Archive'}
                  </Button>
                  <Popconfirm title="Delete this thread and its messages?" okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => remove.mutateAsync(thread.data!.id).then(() => { set({ thread: undefined }); message.success('Thread deleted'); }).catch((e) => message.error(errorMessage(e)))}>
                    <Tooltip title="Delete thread">
                      <Button size="small" danger icon={<DeleteOutlined />} aria-label="Delete thread" />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              }
            >
              <ThreadView key={thread.data.id} thread={thread.data} />
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
